import React, { useState } from 'react';
import { CreditCard, Plus, Trash2, ArrowLeft, Check, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PaymentCardsScreen = () => {
  const navigate = useNavigate();
  const [showAddCard, setShowAddCard] = useState(false);
  const [cards, setCards] = useState([
    {
      id: 1,
      last4: '4242',
      expiryDate: '12/25',
      type: 'visa',
      isDefault: true
    },
    {
      id: 2,
      last4: '8888',
      expiryDate: '09/24',
      type: 'mastercard',
      isDefault: false
    }
  ]);

  const CardForm = () => {
    const [cardDetails, setCardDetails] = useState({
      number: '',
      expiry: '',
      cvv: '',
      name: ''
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      // Handle card addition logic here
      setShowAddCard(false);
    };

    return (
      <div className="fixed inset-0 bg-white z-50">
        <div className="p-4">
          <div className="flex items-center mb-6">
            <button 
              onClick={() => setShowAddCard(false)}
              className="p-2"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600" />
            </button>
            <h2 className="text-xl font-semibold ml-2">Adaugă card nou</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-700 mb-2">Număr Card</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="1234 5678 9012 3456"
                  className="w-full p-4 bg-gray-50 rounded-xl pr-12"
                  maxLength="19"
                  value={cardDetails.number}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\s/g, '');
                    value = value.replace(/(\d{4})/g, '$1 ').trim();
                    setCardDetails({...cardDetails, number: value});
                  }}
                />
                <CreditCard className="absolute right-4 top-4 text-gray-400 w-6 h-6" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 mb-2">Data Expirării</label>
                <input
                  type="text"
                  placeholder="MM/YY"
                  className="w-full p-4 bg-gray-50 rounded-xl"
                  maxLength="5"
                  value={cardDetails.expiry}
                  onChange={(e) => {
                    let value = e.target.value;
                    value = value.replace(/\D/g, '');
                    if (value.length >= 2) {
                      value = value.slice(0, 2) + '/' + value.slice(2);
                    }
                    setCardDetails({...cardDetails, expiry: value});
                  }}
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2">CVV</label>
                <input
                  type="password"
                  placeholder="123"
                  className="w-full p-4 bg-gray-50 rounded-xl"
                  maxLength="3"
                  value={cardDetails.cvv}
                  onChange={(e) => setCardDetails({...cardDetails, cvv: e.target.value})}
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-700 mb-2">Nume pe Card</label>
              <input
                type="text"
                placeholder="NUME PRENUME"
                className="w-full p-4 bg-gray-50 rounded-xl uppercase"
                value={cardDetails.name}
                onChange={(e) => setCardDetails({...cardDetails, name: e.target.value})}
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                className="w-full bg-blue-600 text-white p-4 rounded-xl font-medium hover:bg-blue-700"
              >
                Adaugă Card
              </button>
            </div>
          </form>

          <div className="flex items-center justify-center mt-4 text-gray-600 text-sm">
            <Lock className="w-4 h-4 mr-2" />
            <span>Datele tale sunt securizate</span>
          </div>
        </div>
      </div>
    );
  };

  const handleSetDefault = (cardId) => {
    setCards(cards.map(card => ({
      ...card,
      isDefault: card.id === cardId
    })));
  };

  const handleDeleteCard = (cardId) => {
    setCards(cards.filter(card => card.id !== cardId));
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white p-4 flex items-center shadow-sm">
        <button onClick={() => navigate(-1)} className="p-2">
          <ArrowLeft className="w-6 h-6 text-gray-600" />
        </button>
        <h1 className="text-xl font-semibold text-gray-800 ml-2">
          Cardurile Mele
        </h1>
      </div>

      {/* Cards List */}
      <div className="p-4">
        <div className="space-y-4">
          {cards.map(card => (
            <div
              key={card.id}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center">
                  <CreditCard className="w-6 h-6 text-gray-600 mr-3" />
                  <div>
                    <div className="font-medium text-gray-900">
                      •••• {card.last4}
                    </div>
                    <div className="text-gray-500 text-sm">
                      Expiră: {card.expiryDate}
                    </div>
                  </div>
                </div>
                {card.type === 'visa' ? (
                  <span className="text-blue-600 font-semibold">VISA</span>
                ) : (
                  <span className="text-orange-600 font-semibold">MASTERCARD</span>
                )}
              </div>

              <div className="flex items-center justify-between">
                <button
                  onClick={() => handleSetDefault(card.id)}
                  className={`px-3 py-1 rounded-full text-sm flex items-center ${
                    card.isDefault
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {card.isDefault && <Check className="w-4 h-4 mr-1" />}
                  {card.isDefault ? 'Principal' : 'Setează Principal'}
                </button>
                <button
                  onClick={() => handleDeleteCard(card.id)}
                  className="text-red-600 p-2"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Add New Card Button */}
        <button
          onClick={() => setShowAddCard(true)}
          className="w-full mt-6 bg-gray-100 text-gray-700 p-4 rounded-xl font-medium hover:bg-gray-200 flex items-center justify-center"
        >
          <Plus className="w-5 h-5 mr-2" />
          Adaugă Card Nou
        </button>
      </div>

      {/* Add Card Modal */}
      {showAddCard && <CardForm />}
    </div>
  );
};

export default PaymentCardsScreen;